import { observer } from "mobx-react";
import IconCircleRestocked from "../../../../../components/icons/icon-circle-restocked/icon-circle-restocked";
import RmaPageStore, { IRmaAction, RMA_ACTION_TYPE } from "../../stores/rma-page-store";
import { useState } from "react";
import IconReturnNotRefundExchange from "../../../../../components/icons/icon-return-not-refund-exchange/icon-return-not-refund-exchange";
import RmaActionModal from "../rma-action-modal/rma-action-modal";
import RmaActionConfirm from "../rma-action-confirm/rma-action-confirm";
import { Button } from "../../../../../components/inputs/button/button";
import { BUTTON_VARIANTS } from "../../../../../components/inputs/button-variants";
import MoreActionsMenu from "./more-actions-menu/more-actions-menu";
import RmaActionRestockModal from "../rma-action-restock-modal/rma-action-restock-modal";
import IconPolicyGroupReturn from "../../../../../components/icons/icon-policy-group-return/icon-policy-group-return";
import RefundCalculatorDrawer from "../../components/refund-calculator-drawer/refund-calculator-drawer";

import './rma-actions-toolbar.scss';
import currentPortalStore from "../../../../../stores/current-portal-store";
import currentAccountStore from "../../../../accounts/stores/current-account-store";
import managerApprovalModalStore from "../../../../../manager-approval-modal/manager-approval-modal-store";
import { IRMAEmailLog } from "../../rma-types";
import ResendStatusModal from "../resend-status-email/resend-status-email-modal/resend-status-email-modal";
import RmaResolveErrorsModal from "../rma-resolve-errors-modal/rma-resolve-errors-modal";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import RmaActionValidateAllModal from "../rma-action-validate-all-modal/rma-action-validate-all-modal";
import dashboardTranslationsStore from "../../../../../dashboard-translation/stores/dashboard-translations-store";
import BaseLayoutStickyToolbar
    from "../../../../../components/layouts/base-page-layout/base-layout-sticky-toolbar/base-layout-sticky-toolbar";
import RejectItemPopup from "../rma-reject-items-modal/reject-item-popup";
import rmsMixpanelStore from "../../../../../stores/rms-mixpanel-store";

interface IProps {
    rma: RmaPageStore;
}

export const RmaActionsToolbar: React.FC<IProps> = observer(({ rma }) => {
    const currentRmaAction: IRmaAction|null = rma.rmaActionsToolbarStore().getCurrentAction();
    const setCurrentRmaAction = rma.rmaActionsToolbarStore().setCurrentAction;

    const nextAction = rma.nextAction();
    const releaseExchangeAction = rma.releaseExchangeAction();
    const restockAction = rma.restockAction();
    const refundAction = rma.refundAction();
    const validateAllAction = rma.validateAllAction();

    const { getTranslation } = useDashboardTranslation();
    const scope = 'dashboard-returns-rma-card';

    const IconReturnNotRefundExchangeWhite = () => (<IconReturnNotRefundExchange color="var(--color-neutral-1-white)" />);

    /* Actions disables when no customer or order to make sure we have everything we need, specifically we need an email for the approve modal */
    const areActionsDisabled = currentPortalStore.isLoading || currentAccountStore.isLoading || !rma.orderStore || !rma.customerCaseStore?.customer;
    const getCurrentActionModal = () => {
        /** should not happen, since we call this only if there is action */
        if (!currentRmaAction) {
            return null;
        }

        switch (currentRmaAction.type) {
            case RMA_ACTION_TYPE.APPROVE:
            case RMA_ACTION_TYPE.RECEIVE_SHIPMENT:
            case RMA_ACTION_TYPE.REOPEN:
            case RMA_ACTION_TYPE.REJECT:
            case RMA_ACTION_TYPE.CANCEL:
                return <RmaActionModal
                    rma={rma}
                    rmaAction={currentRmaAction}
                    onCloseClick={handleKeepCurrentStatusClick}
                />;
            case RMA_ACTION_TYPE.MARK_AS_DONE:
                return <RmaActionConfirm
                    rma={rma}
                    rmaAction={currentRmaAction}
                    onCloseClick={handleKeepCurrentStatusClick}
                />;
            case RMA_ACTION_TYPE.RESTOCK:
                return <RmaActionRestockModal
                    rma={rma}
                    rmaAction={currentRmaAction}
                    onCloseClick={() => setCurrentRmaAction(null)}
                />;
            case RMA_ACTION_TYPE.REFUND:
                sendMixpanelEvent(RMA_ACTION_TYPE.REFUND);
                if (rma.refundCalculatorStore?.isLoading) {
                    return null;
                }
                return <RefundCalculatorDrawer
                    closeAction={() => setCurrentRmaAction(null)}
                    rma={rma}
                />
            case RMA_ACTION_TYPE.RESOLVE_ERRORS:
                return <RmaResolveErrorsModal
                        rmaAction={currentRmaAction}
                        onCloseClick={() => setCurrentRmaAction(null)}
                    />
            case RMA_ACTION_TYPE.VALIDATE_ALL_ITEMS:
                return <RmaActionValidateAllModal
                        rma={rma}
                        rmaAction={currentRmaAction}
                        onCloseClick={() => setCurrentRmaAction(null)}
                    />
            case RMA_ACTION_TYPE.REJECT_ITEM:
                return <RejectItemPopup
                    rmaAction={currentRmaAction}
                    onCloseCallback={() => {setCurrentRmaAction(null)}}
                    rma={rma}
                />
        }
    }

    const onRefundClick = () => {
        setCurrentRmaAction(refundAction);
        refundAction?.store.submitAction();
    }

    const onValidateAllClick = () => {
        setCurrentRmaAction(validateAllAction);
    }

    const sendMixpanelEvent = (actionType: RMA_ACTION_TYPE) => {
        switch (actionType) {
            case RMA_ACTION_TYPE.RESTOCK:
                rmsMixpanelStore.trackEvent('d&_rma_restock_button');
                break;
            case RMA_ACTION_TYPE.REFUND:
                rmsMixpanelStore.trackEvent('d&_refund_credit_button');
                break;
        }
    };

    const handleKeepCurrentStatusClick = () => {
        rmsMixpanelStore.trackEvent('d&_rma_keep_current_status');
        setCurrentRmaAction(null);
    };

    const handleNextAction = async () => {
        if (!nextAction) {
            return null;
        }

        sendMixpanelEvent(nextAction.type);

        if(nextAction.type === RMA_ACTION_TYPE.APPROVE && rma.shouldRequestManagersApproval()) {
            const credsResult = await managerApprovalModalStore.collectCredentials();
            if (!credsResult.credentials) {
                return;
            }

            rma.setManagerCredentials(credsResult.credentials);
        }
        
        setCurrentRmaAction(nextAction);
    }

    const handleRestockClick = () => {
        sendMixpanelEvent(RMA_ACTION_TYPE.RESTOCK);
        setCurrentRmaAction(restockAction);
    }

    const handleReleaseExchangeClick = () => {
        sendMixpanelEvent(RMA_ACTION_TYPE.RELEASE_EXCHANGE);
        releaseExchangeAction?.store?.submitAction();
    }

    const [ currentEmailLog, setCurrentEmailLog ] = useState<IRMAEmailLog|null>(null);

    return (
        <>
            <BaseLayoutStickyToolbar>
                {refundAction && rma.isRefundAllowed() &&
                    <Button
                        variant={BUTTON_VARIANTS.PrimaryAccent}
                        leftIcon={() => <IconPolicyGroupReturn color={'var(--color-neutral-1-white)'}/>}
                        text={getTranslation(refundAction.buttonTextTranslationKey, scope)}
                        onClick={() => onRefundClick()}
                        hasLoader={true}
                        disabled={areActionsDisabled}
                        isLoading={rma.refundCalculatorStore?.isLoading}
                    />
                }

                {restockAction &&
                    <Button
                        variant={BUTTON_VARIANTS.PrimaryAccent}
                        leftIcon={() => <IconCircleRestocked color="var(--color-neutral-1-white)" />}
                        text={getTranslation(restockAction.name, scope)}
                        onClick={handleRestockClick}
                        disabled={restockAction.disabled || areActionsDisabled}
                    />
                }

                {releaseExchangeAction &&
                    <Button
                        variant={BUTTON_VARIANTS.PrimaryAccent}
                        leftIcon={IconReturnNotRefundExchangeWhite}
                        text={getTranslation(releaseExchangeAction.name, scope)}
                        disabled={releaseExchangeAction.disabled || areActionsDisabled}
                        hasLoader={true}
                        isLoading={rma.isReleasingExchnage}
                        onClick={handleReleaseExchangeClick}
                    />
                }

                {(validateAllAction && rma.isValidateAllEnabled && !dashboardTranslationsStore.isLoading) &&
                    <Button
                        variant={BUTTON_VARIANTS.PrimaryAccent}
                        text={getTranslation(validateAllAction.name, scope)}
                        disabled={validateAllAction.disabled || areActionsDisabled}
                        onClick={onValidateAllClick}
                    />
                }

                <div className="rma-actions-toolbar-right">
                    <MoreActionsMenu rma={rma} setCurrentRmaAction={setCurrentRmaAction} setCurrentEmailLog={setCurrentEmailLog} />
                    { currentEmailLog &&  <ResendStatusModal rma={rma} emailLog={currentEmailLog} onCloseClick={() => setCurrentEmailLog(null)} /> }

                    {!!nextAction &&
                        <Button
                            variant={BUTTON_VARIANTS.PrimaryAccent}
                            text={getTranslation(nextAction.name, scope)}
                            disabled={nextAction.disabled || areActionsDisabled}
                            onClick={handleNextAction}
                        />
                    }
                </div>
            </BaseLayoutStickyToolbar>

            {!!currentRmaAction && getCurrentActionModal()}
        </>
    );
});

export default RmaActionsToolbar; 